import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ParkingGate,
  ParkingGateType,
  ParkingVehicleType,
} from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-gate',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-gate.component.html',
  styleUrl: './modal-parking-gate.component.css',
})
export class ModalParkingGateComponent implements OnInit {
  @Input() itemId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingGate;
  gateTypes: Array<{ id: number; name: string }> = [];
  vehicleTypes: Array<{ id: number; name: string }> = [];
  parentGates: Array<{ id: number; name: string }> = [];
  connectDateLocal = '';
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyGate(this.siteId || null, this.siteName);
    this.loadDropdowns();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  onConnectDateChange(value: string): void {
    this.connectDateLocal = value;
    this.formData.connectDate = value ? new Date(value) : null;
  }

  onSave(): void {
    if (!this.formData.name?.trim()) { this.toast.error('กรุณาระบุชื่อ'); return; }
    if (!this.siteId) { this.toast.error('กรุณาเลือกลานจาก Sidebar'); return; }
    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์แก้ไข'); return; }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์สร้าง'); return; }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) { this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู'); return; }

    this.formData.siteId = this.siteId;
    this.formData.siteName = this.siteName;
    if (this.connectDateLocal) {
      this.formData.connectDate = new Date(this.connectDateLocal);
    }

    const payload = this.mapper.mapGatePayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingGate(this.itemId, payload)
      : this.api.insertParkingGate(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDropdowns(): void {
    this.api.getParkingGateTypeForTable({ page: 1, pageSize: 100 }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res, (item) => this.mapper.mapGateType(item as never));
        this.gateTypes = mapped.data.map((g: ParkingGateType) => ({ id: g.id, name: g.name }));
      },
      error: () => { this.gateTypes = []; },
    });

    this.api.getParkingVehicleTypeForTable({ page: 1, pageSize: 100 }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res, (item) => this.mapper.mapVehicleType(item as never));
        this.vehicleTypes = mapped.data.map((v: ParkingVehicleType) => ({ id: v.id, name: v.name }));
      },
      error: () => { this.vehicleTypes = []; },
    });

    if (this.siteId) {
      this.api.getParkingGateForTable({ page: 1, pageSize: 100, siteId: this.siteId }).subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res, (item) => this.mapper.mapGate(item as never));
          this.parentGates = mapped.data
            .filter((g: ParkingGate) => g.id !== this.itemId)
            .map((g: ParkingGate) => ({ id: g.id, name: g.name }));
        },
        error: () => { this.parentGates = []; },
      });
    }
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingGateById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapGate(res as never);
        this.connectDateLocal = this.toLocalInput(this.formData.connectDate);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private toLocalInput(date: Date | null): string {
    if (!date) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private resolveUserId(): number {
    return Number(localStorage.getItem('userId')) || Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
